import { WebsocketProvider } from 'y-websocket';
import type * as Y from 'yjs';

/**
 * Connection status surfaced to the UI.
 *
 * - `disabled`  — no server URL configured (or provider destroyed); the
 *                 store is operating in local-only mode.
 * - `connecting`— a websocket has been opened but the server has not
 *                 acknowledged the upgrade yet.
 * - `connected` — websocket is OPEN and the Yjs sync handshake has been
 *                 sent. The doc is converging in real time with the
 *                 remote room.
 * - `disconnected` — the underlying socket closed unexpectedly. The
 *                 internal `WebsocketProvider` will retry with
 *                 exponential backoff up to `maxBackoffTime` (~30s).
 */
export type SyncStatus = 'disabled' | 'connecting' | 'connected' | 'disconnected';

export interface SyncProviderOptions {
  /** The shared Y.Doc to sync. */
  doc: Y.Doc;
  /** Logical room name; appended as a path segment to `serverUrl`. */
  room: string;
  /**
   * Sync server URL. When omitted (or null) the provider stays in
   * `disabled` mode and never opens a socket. This lets us run the
   * app fully offline in unit tests and during local development.
   */
  serverUrl?: string | null;
  /** Optional WebSocket constructor for tests. */
  WebSocketPolyfill?: typeof WebSocket;
  /**
   * Disable cross-tab BroadcastChannel sync. Useful in tests where
   * multiple suites in the same process would otherwise leak state.
   */
  disableBc?: boolean;
}

/**
 * Thin wrapper around `y-websocket`'s `WebsocketProvider` that:
 *   1. Lets us inject a fake WebSocket constructor in tests
 *   2. Surfaces connection status as a tiny observable so React can
 *      render a "connecting / online / offline" indicator
 *   3. Keeps the rest of the app from depending on `y-websocket`
 *      directly — if we ever swap providers, only this file changes.
 *
 * The reconnect/backoff logic itself lives inside `WebsocketProvider`
 * (exponential backoff capped at ~30s). We expose `destroy()` so the
 * doc-store can tear it down when the user resets local data.
 */
export class SyncProvider {
  private readonly listeners = new Set<(status: SyncStatus) => void>();
  private provider: WebsocketProvider | undefined;
  private currentStatus: SyncStatus;

  constructor(options: SyncProviderOptions) {
    if (!options.serverUrl) {
      this.currentStatus = 'disabled';
      return;
    }

    this.currentStatus = 'connecting';
    const providerOpts: ConstructorParameters<typeof WebsocketProvider>[3] = {
      disableBc: options.disableBc ?? false,
    };
    if (options.WebSocketPolyfill !== undefined) {
      providerOpts.WebSocketPolyfill = options.WebSocketPolyfill;
    }
    const provider = new WebsocketProvider(
      options.serverUrl,
      options.room,
      options.doc,
      providerOpts,
    );
    provider.on('status', ({ status }: { status: SyncStatus }) => {
      this.setStatus(status);
    });
    this.provider = provider;
  }

  get status(): SyncStatus {
    return this.currentStatus;
  }

  /** Subscribe to status changes. Returns an unsubscribe function. */
  subscribe(listener: (status: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Tear down the underlying socket and stop emitting status events. */
  destroy(): void {
    if (this.provider) {
      this.provider.destroy();
      this.provider = undefined;
    }
    this.currentStatus = 'disabled';
    this.listeners.clear();
  }

  private setStatus(next: SyncStatus): void {
    if (this.currentStatus === next) return;
    this.currentStatus = next;
    for (const listener of this.listeners) listener(next);
  }
}
